import React from 'react'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, CircularProgress, IconButton, MenuItem, Stack, Tooltip } from '@mui/material'
import { Form, useFormikContext } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import SelectField from '../../components/form/SelectField'
import { EntityType, PartialUser } from '../../__generated__/graphql'
import mapEnumForSelect from '../../utils/mapEnumForSelect'
import { getActivityFeedEntityTypeLabel } from './activityFeedEntity'
import AsyncAutoCompleteUsers from '../../components/form/AsyncAutoCompleteUsers'
import AsyncAutoCompleteCompanies from '../../components/form/AsyncAutoCompleteCompanies'
import Toolbar from '../../components/toolbar/Toolbar'
import ToolbarFilterRow, { ToolbarFilterField } from '../../components/toolbar/ToolbarFilterRow'

interface FilterProps {
  onReset: () => void
  onRefresh: () => void
  isRefetching?: boolean
  userList: Array<PartialUser>
  canFilterCompany: boolean
  toolbarEnd?: React.ReactNode
}

const Filter: React.FC<FilterProps> = ({
  onReset,
  onRefresh,
  isRefetching = false,
  canFilterCompany,
  toolbarEnd,
}): JSX.Element => {
  const t = useAppTranslations()
  const formik = useFormikContext()

  const entityTypeOptions = mapEnumForSelect(EntityType).map((entityType, i) => (
    <MenuItem value={entityType.value.toString()} key={i}>
      {getActivityFeedEntityTypeLabel(entityType.value as EntityType, t)}
    </MenuItem>
  ))

  return (
    <Form>
      <Toolbar
        alwaysVisible
        start={
          <ToolbarFilterRow>
            <ToolbarFilterField minWidth={180}>
              <SelectField withNone={false} name={'entityType'} label={t('commons.entityType')} variant="outlined">
                <MenuItem value={'all'}>{t('search.defaultAllFilter')}</MenuItem>
                {entityTypeOptions}
              </SelectField>
            </ToolbarFilterField>

            <ToolbarFilterField minWidth={220}>
              <AsyncAutoCompleteUsers />
            </ToolbarFilterField>

            {canFilterCompany && (
              <ToolbarFilterField minWidth={220}>
                <AsyncAutoCompleteCompanies />
              </ToolbarFilterField>
            )}
          </ToolbarFilterRow>
        }
        end={
          <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Button variant="text" onClick={onReset} disabled={!formik.dirty}>
              {t('search.resetFilters')}
            </Button>
            {toolbarEnd}
            <Tooltip title={t('activity.refresh')}>
              <span>
                <IconButton disabled={isRefetching} onClick={onRefresh} size="small">
                  {isRefetching ? <CircularProgress size={16} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
        style="table"
      />
    </Form>
  )
}

export default Filter
